import { ChangeEvent, Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';

import {
  ActionIcon,
  Badge,
  Box,
  Button,
  Flex,
  Group,
  HoverCard,
  Loader,
  Switch,
  Text,
  Title,
  Tooltip,
  UnstyledButton
} from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { notifications } from '@mantine/notifications';
import { IconCalendar, IconCurrencyDollar } from '@tabler/icons-react';
import dayjs from 'dayjs';
import { useSelector } from 'react-redux';
import { components, OptionProps } from 'react-select';

import { RootState } from '@/store';
import { useAppDispatch } from '@/store/hooks';
import { selectPatientSettings } from '@/store/selectors/transmissionReportPatientSelectors';
import {
  getAreFieldsEditable,
  getIsPractitioner
} from '@/store/selectors/transmissionReportSelectors';
import {
  appointmentApiSlice,
  useLinkAppointmentToTransmissionMutation
} from '@/store/slices/api/appointmentApiSlice';
import {
  billingApiSlice,
  useUpdateBillingMutation,
  useUpdateIsBillableMutation
} from '@/store/slices/api/billingApiSlice';
import { useGetOrgByIdQuery } from '@/store/slices/api/organizationApiSlice';
import { useGetAllFlagsQuery } from '@/store/slices/api/featureFlagsApiSlice';
import { useGetLastBillableDatesQuery } from '@/store/slices/api/transmissionReport/reportsPatientApiSlice';
import {
  transmissionReportApiSlice,
  useGetTransmissionReportDetailsQuery,
  usePatchTransmissionReportMutation,
  useUpdateTransmissionTypeMutation
} from '@/store/slices/api/transmissionReport/transmissionReportsApiSlice';
import { setEmrEncounterId } from '@/store/slices/form/transmissionReportFormSlice';
import { getReportName, isReportInClinic } from '@/utils/constants';
import { API_SLICE } from '@/utils/constants/apiEndpoints';
import {
  PROFESSIONAL_OPTIONS_IN_CLINIC,
  PROFESSIONAL_OPTIONS_MANUAL,
  PROFESSIONAL_OPTIONS_REMOTE,
  TECHNICAL_OPTIONS,
  TECHNICAL_OPTIONS_HEART_FAILURE,
  TECHNICAL_OPTIONS_ILR,
  TECHNICAL_OPTIONS_IN_CLINIC
} from '@/utils/constants/billingCodes.tsx';
import formatDate, { formatUtcDate } from '@/utils/formatDate';
import { getDisplaySignatures } from '@/utils/reportSignatures';
import { useNavigationReportIds } from '@/hooks/useNavigationReportIds';
import { useReportListDrawer } from '@/hooks/useReportListDrawer';
import InfoIcon from '@/assets/icons/InfoIcon';

import { LabelAndValue } from '@/types/custom';
import { Appointment } from '@/types/matchDevice';
import {
  ReportCategoryType,
  ReportColor,
  ReportType,
  SignatureStatus
} from '@/types/transmissionReport';
import ConfirmationModal from '@/ui/ConfirmationModal';
import Dropdown from '@/ui/Dropdown';
import LabelText from '@/ui/LabelText';
import SkeletonGrid from '@/ui/Skeleton';
import SwitchTabs from '@/ui/SwitchTabs';

import { encounterValueForOpenReport } from '../encounterField';
import { getAlertTypeMeta } from '../utils/collapsedBannerMeta';

import { AppointmentSelection } from './AppointmentSelection';
import { EncounterIdField, EncounterOption } from './EncounterIdField';
import styles from './ReportDetails.module.scss';
import TriageAuditLogModal from './TriageAuditLogModal';
import { TransmissionSelection } from './TranmissionSelection';
import { useSyncLinkedTaskOntoReport } from './useSyncLinkedTaskOntoReport';

export const reportTypeOptions: LabelAndValue[] = [
  { label: 'Non-routine', value: 'ALERT_ONLY' },
  { label: 'Routine', value: 'ROUTINE' },
  { label: 'Heart Failure', value: 'HEART_FAILURE' },
  { label: 'Combo (HF & Routine)', value: 'COMBO' },
  { label: 'ERI Tracking', value: 'ERI_TRACKING' },
  { label: 'Physician Request', value: 'PHYSICIAN_REQUESTED' }
];

export const alertColorTypeOptions: LabelAndValue[] = [
  { label: 'Red', value: ReportColor.Red },
  { label: 'Yellow', value: ReportColor.Yellow },
  { label: 'No Alert', value: ReportColor.Green },
  { label: 'Untriaged', value: ReportColor.Gray }
];

interface ReportDetailsProps {
  setIsOperationInProgress?: (isInProgress: boolean) => void;
}

type BadgeProps = {
  color: string;
  label: string;
};

const getReportTypeLabel = (reportType: string) =>
  reportTypeOptions.find((o) => o.value === reportType)?.label ?? reportType;

const ReportDetails = ({ setIsOperationInProgress }: ReportDetailsProps) => {
  const { id } = useParams();
  const reportId = id ? parseInt(id) : 0;

  const [isUpdatingBillingDate, setIsUpdatingBillingDate] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isTriageModalOpen, setIsTriageModalOpen] = useState(false);
  const [pendingAppointmentType, setPendingAppointmentType] = useState<string | null>(null);
  const [updateTransmissionType] = useUpdateTransmissionTypeMutation();
  const { isLoading, isError, data, refetch } = useGetTransmissionReportDetailsQuery(reportId);
  useSyncLinkedTaskOntoReport(reportId, setIsOperationInProgress);
  const areFieldsEditable = useSelector((state: RootState) => getAreFieldsEditable(state, false));

  const report = data?.transmissionReport;
  const patientSettings = useSelector((state: RootState) =>
    selectPatientSettings(state, report?.patient?.id ?? '')
  );

  const [encounterId, setEncounterId] = useState<string>(report?.emrEncounterId ?? '');

  const { data: clinicData } = useGetOrgByIdQuery(report?.patient?.clinic?.id ?? '', {
    skip: !report?.patient?.clinic?.id
  });

  const [isBillable, setIsBillable] = useState(report?.billing?.isBillable ?? false);
  const [alertColorType, setAlertColorType] = useState(
    report?.color === 'GREEN' || report?.color === 'GRAY' ? '' : report?.color
  );

  const { refetch: refetchNavigationReportIds } = useNavigationReportIds({ reportId });

  const [updateBilling] = useUpdateBillingMutation();

  const shouldShowEncounterId =
    report?.account?.enableEncounterId ?? report?.patient?.clinic?.enableEncounterId ?? false;
  const { data: featureFlags, isSuccess: featureFlagsLoaded } = useGetAllFlagsQuery();
  const isInternalUser = useSelector(
    (state: RootState) => state.auth.user?.role?.includes('internal') ?? false
  );
  const isEditSchedulesEnabled = featureFlags?.includes('edit_schedules') ?? false;
  const isAutoTriagedEnabled =
    clinicData?.organization?.isAutoTriageEnabled ??
    clinicData?.organization?.parent?.isAutoTriageEnabled ??
    false;
  const showAiTriageButton = Boolean(
    isInternalUser && report?.transmission?.autoTriageEvaluationId && isAutoTriagedEnabled
  );
  const isAppointmentManagedInRs =
    report?.account?.isAppointmentManagedInRs ??
    report?.patient?.clinic?.isAppointmentManagedInRs ??
    false;
  const showAppointmentField = Boolean(
    report?.matchDevice?.id && isEditSchedulesEnabled && isAppointmentManagedInRs
  );
  const requireVisitToSign = !useSelector(getIsPractitioner);

  const shouldDisplayBillingCodesDropdowns =
    clinicData?.organization?.parent?.displayBillingCodesDropdowns ??
    clinicData?.organization?.displayBillingCodesDropdowns ??
    false;

  const [updateIsBillable, { isLoading: isBillableUpdating }] = useUpdateIsBillableMutation();
  const [updateTransmissionReport] = usePatchTransmissionReportMutation();
  const [linkAppointmentToTransmission] = useLinkAppointmentToTransmissionMutation();

  const [professionalCodes, setProfessionalCodes] = useState<string[]>([]);
  const [technicalCodes, setTechnicalCodes] = useState<string[]>([]);
  const [reportType, setReportType] = useState<ReportCategoryType | null>(null);
  const [appointment, setAppointment] = useState<Appointment | null>(report?.appointment ?? null);
  const [eligibleAppointments, setEligibleAppointments] = useState<Appointment[]>([]);
  const [appointmentLocked, setAppointmentLocked] = useState(false);
  const [isAppointmentTypeLoading, setIsAppointmentTypeLoading] = useState(false);

  const { data: lastBillableDates } = useGetLastBillableDatesQuery(
    { patientId: String(report?.patient?.id), transmissionReportId: reportId },
    { skip: !report?.patient?.id || !reportId }
  );

  const lastBillableEntry = useMemo(() => {
    if (!lastBillableDates || !reportType) return null;
    return lastBillableDates.find((entry) => entry.reportType === reportType) ?? null;
  }, [lastBillableDates, reportType]);

  const recentBillings = useMemo(() => {
    if (!lastBillableDates) return [];
    return [...lastBillableDates]
      .filter((entry) => entry.lastBillingDate)
      .sort(
        (a, b) =>
          new Date(b.lastBillingDate as string).getTime() -
          new Date(a.lastBillingDate as string).getTime()
      )
      .slice(0, 5);
  }, [lastBillableDates]);

  const { moveReport } = useReportListDrawer();
  const dispatch = useAppDispatch();

  const skipNextEffect = useRef(false);
  const lastReportId = useRef<number | null>(null);
  const didFillEncounter = useRef(false);
  const keepAppointmentId = useRef<string | null>(report?.appointment?.id ?? null);
  const billingId = report?.billing?.id ?? '';

  useEffect(() => {
    if (!report) return;

    const currentReportId = report.id;
    const isNewReport = lastReportId.current !== currentReportId;

    if (isNewReport) {
      didFillEncounter.current = false;
      setAppointmentLocked(false);
      keepAppointmentId.current = report.appointment?.id ?? null;
    }

    if (skipNextEffect.current && !isNewReport) {
      skipNextEffect.current = false;
      return;
    }

    setProfessionalCodes(report?.billing?.professionalCodes?.map((code) => code.value) ?? []);
    setTechnicalCodes(report?.billing?.technicalCodes ?? []);
    setIsBillable(report?.billing?.isBillable ?? false);
    setAlertColorType(report?.color);
    setReportType(report?.reportType);
    setAppointment((current) => {
      const incoming = report.appointment ?? null;
      if (incoming) return incoming;
      if (current?.id && current.id === keepAppointmentId.current) return current;
      return null;
    });

    lastReportId.current = currentReportId;
  }, [report]);

  useEffect(() => {
    if (report?.id !== reportId) didFillEncounter.current = false;
    setEncounterId(encounterValueForOpenReport(reportId, report?.id, report?.emrEncounterId));
  }, [report?.emrEncounterId, report?.id, reportId]);

  const BillingCodeOption = (props: OptionProps<any, true>) => {
    const { data } = props;
    const { isSelected } = props;
    const shortDescription = data.shortDescription ?? '';
    const description = data.description ?? '';
    return (
      <Box>
        <components.Option {...props}>
          <Group gap={0} align="center">
            <Text fw={400} fz="xs" mr={'3'}>
              {data.label}
            </Text>
            {description && (
              <Tooltip
                position="bottom-start"
                withArrow
                multiline
                w={280}
                label={description}
                withinPortal
                zIndex={9999}
                styles={{
                  tooltip: {
                    backgroundColor: '#1a1a2e',
                    color: 'white',
                    fontSize: '14px',
                    padding: '12px 16px',
                    borderRadius: '8px',
                    lineHeight: 1.4
                  },
                  arrow: { backgroundColor: '#1a1a2e' }
                }}>
                <span
                  style={{
                    display: 'flex',
                    cursor: 'pointer'
                  }}>
                  {isSelected ? <InfoIcon color="#fff" /> : <InfoIcon color="#1a1a2e" />}
                </span>
              </Tooltip>
            )}
          </Group>
          <Text fz="xs" c="#9E8D94">
            {shortDescription}
          </Text>
        </components.Option>
      </Box>
    );
  };

  const handleReportTypeChange = async (option: LabelAndValue | null) => {
    if (!option) return;
    const newReportType = option.value as ReportCategoryType;
    setReportType(newReportType);

    if (setIsOperationInProgress) {
      setIsOperationInProgress(true);
    }

    try {
      await updateTransmissionReport({ id: reportId, reportType: newReportType }).unwrap();
      const { data: updatedData } = await refetch();

      setProfessionalCodes(
        updatedData?.transmissionReport?.billing?.professionalCodes?.map((code) => code.value) ?? []
      );
      setTechnicalCodes(updatedData?.transmissionReport?.billing?.technicalCodes ?? []);
      dispatch(billingApiSlice.util.invalidateTags([{ type: API_SLICE.Billing, id: reportId }]));
      skipNextEffect.current = true;

      notifications.show({
        title: 'Success',
        message: 'Report Type Changed Successfully',
        color: 'green'
      });
    } catch (error) {
      skipNextEffect.current = false;
      console.error('Error changing report type:', error);
      notifications.show({ title: 'Error', message: 'Failed to change report type', color: 'red' });
    } finally {
      if (setIsOperationInProgress) {
        setIsOperationInProgress(false);
      }
    }
  };

  const handleAlertTypeChange = async (value: LabelAndValue | LabelAndValue[] | null) => {
    const selectedValue = Array.isArray(value) ? value[0]?.value : value?.value;
    setAlertColorType(selectedValue);

    const previousColor = report?.color as ReportColor;
    const newColor = selectedValue === '' ? ReportColor.Green : (selectedValue as ReportColor);

    try {
      await updateTransmissionReport({ id: reportId, color: newColor }).unwrap();
      skipNextEffect.current = true;
      moveReport(reportId, previousColor, newColor);
      await refetchNavigationReportIds();
      notifications.show({
        title: 'Success',
        message: 'Alert Type Changed Successfully',
        color: 'green'
      });
    } catch (error) {
      skipNextEffect.current = false;
      console.error('Error changing Alert type:', error);
      notifications.show({ title: 'Error', message: 'Failed to update Alert type', color: 'red' });
    }
  };

  const handleDateChange = async (date: Date | null) => {
    if (!date || !reportId || !billingId) return;

    try {
      setIsUpdatingBillingDate(true);
      const formattedDate = formatDate(date);
      await updateBilling({
        billingId,
        data: { transmissionReportId: reportId, billingDate: formattedDate }
      }).unwrap();
      await refetch();
      notifications.show({
        title: 'Success',
        message: 'Billing date updated successfully',
        color: 'green'
      });
    } catch (error) {
      console.error('Error updating billing date:', error);
      notifications.show({
        title: 'Error',
        message: 'Failed to update billing date',
        color: 'red'
      });
    } finally {
      setIsUpdatingBillingDate(false);
    }
  };

  const handleIsBillableChange = async (e?: ChangeEvent<HTMLInputElement>) => {
    const isBillable = e?.target.checked ?? false;
    setIsBillable(isBillable);

    if (setIsOperationInProgress) {
      setIsOperationInProgress(true);
    }

    try {
      await updateIsBillable({
        billingId,
        data: { transmissionReportId: report?.id ?? 0, isBillable }
      }).unwrap();

      const { data: updatedData } = await refetch();
      setProfessionalCodes(
        updatedData?.transmissionReport?.billing?.professionalCodes?.map((code) => code.value) ?? []
      );
      setTechnicalCodes(updatedData?.transmissionReport?.billing?.technicalCodes ?? []);
      skipNextEffect.current = true;

      notifications.show({
        title: 'Success',
        message: `Successfully made the report ${isBillable ? 'Billable' : 'Non billable'}`,
        color: 'green'
      });
    } catch (error) {
      skipNextEffect.current = false;
      const message = `Error updating report to ${isBillable ? 'Billable' : 'Non billable'}`;
      console.error(message, error);
      notifications.show({ title: 'Error', message, color: 'red' });
    } finally {
      if (setIsOperationInProgress) {
        setIsOperationInProgress(false);
      }
    }
  };

  const handleBillingCodesChange = async (
    name: 'professionalCodes' | 'technicalCodes',
    value: LabelAndValue | LabelAndValue[] | null
  ) => {
    if (!reportId) return;

    let codes;
    if (name === 'professionalCodes') {
      const selectedValues = Array.isArray(value)
        ? value.map((item) => item.value)
        : value
          ? [value.value]
          : [];
      codes = selectedValues.map((code) => ({ value: code }));
    } else {
      const selectedValues = Array.isArray(value)
        ? value.map((item) => item)
        : value
          ? [value.value]
          : [];
      codes = selectedValues;
    }

    try {
      await updateBilling({
        billingId,
        data: { transmissionReportId: reportId, [name]: codes }
      }).unwrap();
      await refetch();
      skipNextEffect.current = true;
      notifications.show({
        title: 'Success',
        message: 'Billing codes updated successfully',
        color: 'green'
      });
    } catch (error) {
      skipNextEffect.current = false;
      console.error('Error updating billing codes:', error);
      notifications.show({
        title: 'Error',
        message: 'Failed to update billing codes',
        color: 'red'
      });
    }
  };

  const persistEncounterId = async (value: string) => {
    if (report?.id !== reportId) return;
    const trimmed = value.trim();
    setEncounterId(trimmed);
    dispatch(setEmrEncounterId(trimmed));
    await updateTransmissionReport({
      id: reportId,
      emrEncounterId: trimmed || null
    }).unwrap();
    dispatch(
      transmissionReportApiSlice.util.updateQueryData(
        'getTransmissionReportDetails',
        reportId,
        (draft) => {
          draft.transmissionReport.emrEncounterId = trimmed || null;
        }
      )
    );
  };

  const saveEnteredEncounter = async (value: string) => {
    const trimmed = value.trim();
    const selectedId = appointment?.id;
    const appointmentEncounter = !selectedId
      ? ''
      : appointment?.emrEncounterId?.trim() ||
        eligibleAppointments.find((item) => item.id === selectedId)?.emrEncounterId?.trim() ||
        (report?.appointment?.id === selectedId ? report.appointment.emrEncounterId?.trim() : '') ||
        '';
    await persistEncounterId(trimmed);
    if (!trimmed || !selectedId || appointmentEncounter) return;

    setAppointment((current) =>
      current?.id === selectedId ? { ...current, emrEncounterId: trimmed } : current
    );
    dispatch(
      transmissionReportApiSlice.util.updateQueryData(
        'getTransmissionReportDetails',
        reportId,
        (draft) => {
          if (draft.transmissionReport.appointment?.id === selectedId) {
            draft.transmissionReport.appointment.emrEncounterId = trimmed;
          }
        }
      )
    );
    const deviceId = report?.matchDevice?.id;
    if (!deviceId) return;
    dispatch(
      appointmentApiSlice.util.updateQueryData('getActiveAppointments', { deviceId }, (draft) => {
        const match = draft.find((item) => item.id === selectedId);
        if (match && !match.emrEncounterId?.trim()) match.emrEncounterId = trimmed;
      })
    );
  };

  const handleAppointmentChange = async (
    option: LabelAndValue | null,
    source?: Appointment | null
  ) => {
    const newAppointmentId = option?.value ?? null;
    const previousAppointment = appointment;
    const nextAppointment = !newAppointmentId
      ? null
      : source ??
        (previousAppointment?.id === newAppointmentId
          ? previousAppointment
          : ({ id: newAppointmentId } as Appointment));
    keepAppointmentId.current = nextAppointment?.id ?? null;
    setAppointment(nextAppointment);

    try {
      await linkAppointmentToTransmission({
        transmissionId: reportId,
        appointmentId: newAppointmentId
      }).unwrap();
      dispatch(
        transmissionReportApiSlice.util.updateQueryData(
          'getTransmissionReportDetails',
          reportId,
          (draft) => {
            draft.transmissionReport.appointment = nextAppointment;
          }
        )
      );
      const deviceId = report?.matchDevice?.id;
      if (deviceId) {
        dispatch(
          appointmentApiSlice.util.updateQueryData(
            'getActiveAppointments',
            { deviceId },
            (draft) => {
              draft.forEach((item) => {
                if (item.linkedTransmissionReport?.id === reportId) {
                  delete item.linkedTransmissionReport;
                }
              });
              if (!newAppointmentId) return;
              const linked = draft.find((item) => item.id === newAppointmentId);
              if (linked) {
                linked.linkedTransmissionReport = {
                  id: reportId
                } as Appointment['linkedTransmissionReport'];
              }
            }
          )
        );
      }
      if (shouldShowEncounterId && source && newAppointmentId) {
        const encounter = source.emrEncounterId?.trim() ?? '';
        if (encounter !== encounterId.trim()) {
          try {
            await persistEncounterId(encounter);
          } catch (encounterError) {
            console.error('Error saving encounter ID from appointment:', encounterError);
            notifications.show({
              title: 'Error',
              message: 'The appointment was linked, but the encounter ID did not save',
              color: 'red'
            });
          }
        }
      }
      skipNextEffect.current = true;
      notifications.show({
        title: 'Success',
        message: newAppointmentId
          ? 'Appointment Linked Successfully to Transmission'
          : 'Appointment Unlinked Successfully from Transmission',
        color: 'green'
      });
    } catch (error: any) {
      skipNextEffect.current = false;
      setAppointment(previousAppointment);
      console.error('Error linking/unlinking appointment to transmission:', error);
      if (error?.data?.details?.errorCode === 'APPOINTMENT_ALREADY_LINKED') {
        notifications.show({
          title: 'Failed to Link Appointment',
          message:
            error?.data?.message ??
            'This appointment already has a transmission associated with it',
          color: 'red'
        });
      } else {
        notifications.show({
          title: 'Error',
          message: newAppointmentId
            ? 'Failed to link appointment to transmission'
            : 'Failed to unlink appointment from transmission',
          color: 'red'
        });
      }
    }
  };

  const handleAppointmentTypeChange = async (id: string) => {
    const newTransmissionType = id === 'in-clinic' ? ReportType.InClinic : ReportType.Remote;
    if (
      report?.signatureStatus === SignatureStatus.Signed ||
      report?.signatureStatus === SignatureStatus.NonPractitionerSigned ||
      getDisplaySignatures(report?.signatures ?? []).length > 0
    ) {
      setPendingAppointmentType(null);
      setIsModalOpen(false);
      notifications.show({
        title: 'Warning',
        message: 'Report Type cannot be changed for Signed Reports',
        color: 'yellow'
      });
      return;
    }
    setIsAppointmentTypeLoading(true);
    try {
      await updateTransmissionType({
        id: reportId,
        transmissionType: newTransmissionType as ReportType
      }).unwrap();

      dispatch(billingApiSlice.util.invalidateTags([{ type: API_SLICE.Billing, id: reportId }]));
      const { data: updatedData } = await refetch();
      setProfessionalCodes(
        updatedData?.transmissionReport?.billing?.professionalCodes?.map((code) => code.value) ?? []
      );
      setTechnicalCodes(updatedData?.transmissionReport?.billing?.technicalCodes ?? []);
      skipNextEffect.current = true;
      notifications.show({
        title: 'Success',
        message: 'Report Type Changed Successfully',
        color: 'green'
      });
      setPendingAppointmentType(null);
      setIsModalOpen(false);
    } catch (error) {
      skipNextEffect.current = false;
      console.error('Error changing report type:', error);
      notifications.show({ title: 'Error', message: 'Failed to change report type', color: 'red' });
    } finally {
      setIsAppointmentTypeLoading(false);
      if (setIsOperationInProgress) {
        setIsOperationInProgress(false);
      }
    }
  };

  const encounterOptions = useMemo<EncounterOption[]>(() => {
    const seen = new Set<string>();
    const options: EncounterOption[] = [];
    const sourceAppointments = appointmentLocked
      ? eligibleAppointments.filter((item) => item.id === appointment?.id)
      : eligibleAppointments;
    for (const item of sourceAppointments) {
      const id = item.emrEncounterId?.trim();
      if (!id || seen.has(id)) continue;
      seen.add(id);
      options.push({ value: id, label: id, appointmentId: item.id });
    }
    return options;
  }, [appointment?.id, appointmentLocked, eligibleAppointments]);

  const fillEncounterFromAppointment = (value?: string | null) => {
    if (report?.id !== reportId || didFillEncounter.current || !shouldShowEncounterId) return;
    if (report?.emrEncounterId?.trim()) {
      didFillEncounter.current = true;
      return;
    }
    const next = (value !== undefined ? value : report?.appointment?.emrEncounterId)?.trim() ?? '';
    didFillEncounter.current = true;
    if (!next) return;
    void persistEncounterId(next).catch((error) => {
      didFillEncounter.current = false;
      console.error('Error saving encounter ID from the linked appointment:', error);
    });
  };

  useEffect(() => {
    if (!featureFlagsLoaded || showAppointmentField) return;
    fillEncounterFromAppointment(undefined);
  }, [
    featureFlagsLoaded,
    report?.appointment?.emrEncounterId,
    report?.id,
    showAppointmentField,
    shouldShowEncounterId
  ]);

  const selectEncounter = (option: EncounterOption) => {
    const source = eligibleAppointments.find((item) => item.id === option.appointmentId);
    if (source && source.id !== appointment?.id) {
      void handleAppointmentChange({ value: source.id, label: option.label }, source);
      return;
    }
    void persistEncounterId(option.value);
  };

  const signatureBadgeMap: Partial<Record<SignatureStatus, BadgeProps>> = {
    [SignatureStatus.NotSigned]: { color: 'gray', label: 'Draft' },
    [SignatureStatus.SentToProvider]: { color: 'gray', label: 'Draft' },
    [SignatureStatus.NonPractitionerSigned]: { color: 'blue', label: 'Pending signature' },
    [SignatureStatus.Signed]: { color: 'green', label: 'Complete' },
    [SignatureStatus.PendingReview]: { color: 'orange', label: 'Pending review' }
  };


  const memoizedData = useMemo(() => {
    const { transmissionReport } = data ?? {};
    if (!transmissionReport) return { professionalOptions: [], technicalOptions: [] };

    const {
      id: reportId,
      transmissionType,
      reportType,
      device: { deviceType } = { deviceType: null }
    } = transmissionReport;
    const isHeartFailure = reportType === ReportCategoryType.Heart_Failure;
    const isInClinic = isReportInClinic(data?.transmissionReport.transmissionType);
    const isRemote = transmissionType === ReportType.Remote;
    const isManual = transmissionType === ReportType.Manual;
    const isILR = deviceType === 'ILR';

    const fetchProfessionalOptions = () => {
      if (isRemote) return PROFESSIONAL_OPTIONS_REMOTE;
      else if (isInClinic) return PROFESSIONAL_OPTIONS_IN_CLINIC;
      else return PROFESSIONAL_OPTIONS_MANUAL;
    };

    const fetchTechnicalOptions = () => {
      if (isInClinic) return TECHNICAL_OPTIONS_IN_CLINIC;
      else if (isILR) return TECHNICAL_OPTIONS_ILR;
      else if (isHeartFailure) return TECHNICAL_OPTIONS_HEART_FAILURE;
      else if (isRemote || isManual) return TECHNICAL_OPTIONS;
      else return TECHNICAL_OPTIONS;
    };

    const convertToLabelValue = (options: any[]): LabelAndValue[] => {
      if (!Array.isArray(options)) return [];
      return options.map((option) => {
        if (option && typeof option === 'object' && 'label' in option && 'value' in option) {
          return option as LabelAndValue;
        }
        if (option && typeof option === 'object') {
          return {
            label: option.description || option.label || option.code || String(option),
            value: option.value || option.code || String(option)
          };
        }
        return { label: String(option), value: String(option) };
      });
    };

    return {
      professionalOptions: convertToLabelValue(fetchProfessionalOptions()),
      technicalOptions: convertToLabelValue(fetchTechnicalOptions()),
      reportId,
      transmissionType,
      isHeartFailure,
      deviceType,
      isInClinic,
      isRemote,
      isManual,
      isILR,
      reportType
    };
  }, [data]);
  const disableCptCodesDropdown =
    (memoizedData.isRemote &&
      reportType &&
      [
        ReportCategoryType.Heart_Failure,
        ReportCategoryType.Routine,
        ReportCategoryType.Combo
      ].includes(reportType)) ??
    false;

  if (isLoading) return <SkeletonGrid height={80} columns={2} rows={1} />;
  if (isError || !report) return <div>Error loading Patient Details</div>;

  const showBillingCodes =
    (shouldDisplayBillingCodesDropdowns && memoizedData.isInClinic) || isBillable;

  const deviceType = (report.matchDevice?.type ?? report.device?.deviceType ?? '').toUpperCase();
  const isIlrDevice = deviceType === 'ILR';
  const clinicScheduling = clinicData?.organization?.settings?.scheduling as
    | { isHfMonitoringEnabled?: boolean }
    | undefined;
  const parentScheduling = clinicData?.organization?.parent?.settings?.scheduling as
    | { isHfMonitoringEnabled?: boolean }
    | undefined;
  const clinicAllowsHf =
    clinicScheduling?.isHfMonitoringEnabled ?? parentScheduling?.isHfMonitoringEnabled ?? false;
  const patientAllowsHf =
    patientSettings?.isHfMonitored ?? report.patient?.settings?.isHfMonitored ?? false;
  const appointmentTypeOptions = reportTypeOptions.filter((option) => {
    if (option.value === reportType) return true;
    if (isIlrDevice && ['HEART_FAILURE', 'COMBO', 'ERI_TRACKING'].includes(option.value)) {
      return false;
    }
    if (!clinicAllowsHf || !patientAllowsHf) {
      if (option.value === 'HEART_FAILURE' || option.value === 'COMBO') return false;
    }
    return true;
  });

  const signatureBadge = signatureBadgeMap[report?.signatureStatus];
  const alertBadge = getAlertTypeMeta(alertColorType || report?.color);

  const name = getReportName(
    report.device?.deviceType ?? '',
    report.transmissionType as ReportType,
    report.color,
    report?.reportType === ReportCategoryType.Heart_Failure
  );

  function handleAppointmentTypeTabClick(value: any): void {
    if (
      (memoizedData.isInClinic && value === 'in-clinic') ||
      (!memoizedData.isInClinic && value === 'remote')
    ) {
      return;
    }
    setPendingAppointmentType(value);
    setIsModalOpen(true);
  }

  function handleCancelAppointmentTypeChange() {
    setIsModalOpen(false);
    setPendingAppointmentType(null);
  }

  const billableControl = (
    <Flex direction="column" align="flex-start" gap={4}>
      <Flex align="center" gap={8}>
        <Switch
          color="red"
          label="Billable"
          checked={isBillable}
          disabled={isBillableUpdating}
          onChange={handleIsBillableChange}
        />
        {isBillableUpdating && <Loader size="xs" />}
      </Flex>
      {lastBillableEntry?.lastBillingDate && (
        <HoverCard width={460} shadow="md" position="bottom-start" withArrow withinPortal openDelay={80}>
          <HoverCard.Target>
            <UnstyledButton style={{ display: 'inline-flex' }}>
              <Text fz="11px" fw={500} c="#81899a" style={{ whiteSpace: 'nowrap' }}>
                Last billed{' '}
                <Text span fz="10px" fw={500} c="#1a1a2e" td="underline">
                  {formatUtcDate(lastBillableEntry.lastBillingDate)}
                </Text>
              </Text>
            </UnstyledButton>
          </HoverCard.Target>
          <HoverCard.Dropdown>
            <div className={styles.billingHistoryGrid}>
              <Text className={styles.billingHistoryHead}>Date of Service</Text>
              <Text className={styles.billingHistoryHead}>Report Type</Text>
              <Text className={styles.billingHistoryHead}>Professional Code</Text>
              <Text className={styles.billingHistoryHead}>Technical Code</Text>
              {recentBillings.map((entry) => (
                <Fragment key={entry.reportType}>
                  <Text className={styles.billingHistoryCell} fw={600}>
                    {formatUtcDate(entry.lastBillingDate as string)}
                  </Text>
                  <Text className={styles.billingHistoryCell}>
                    {getReportTypeLabel(entry.reportType)}
                  </Text>
                  <Text className={styles.billingHistoryCell}>
                    {entry.professionalCodes?.join(', ') || '—'}
                  </Text>
                  <Text className={styles.billingHistoryCell}>
                    {entry.technicalCodes?.join(', ') || '—'}
                  </Text>
                </Fragment>
              ))}
            </div>
          </HoverCard.Dropdown>
        </HoverCard>
      )}
    </Flex>
  );

  return (
    <div
      className={styles.root}
      style={!areFieldsEditable ? { opacity: 0.6, pointerEvents: 'none' } : {}}>
      <div>
        <div className={styles.reportLayout}>
          <div className={styles.titleCluster}>
              <Title className={styles.title} fw={'500'}>
                {name}
              </Title>
              <div className={styles.statusGroup}>
              {alertBadge && (
                <Badge color={alertBadge.color} variant="light" radius="sm" size="lg">
                  {alertBadge.label}
                </Badge>
              )}
              {signatureBadge && (
                <Badge color={signatureBadge.color} variant="dot" size="lg">
                  {signatureBadge.label}
                </Badge>
              )}
              {showAiTriageButton && (
                <Button
                  className={styles.triageButton}
                  variant="light"
                  color="plum"
                  size="compact-xs"
                  radius="xl"
                  onClick={() => setIsTriageModalOpen(true)}>
                  View AI triage
                </Button>
              )}
              </div>
              {report?.billing?.isAppDeterminedBillable && (
                <Tooltip
                  label={
                    <Text fz={'xs'} fw={500}>
                      Synergy has determined this report to be billable.
                    </Text>
                  }
                  color="plum"
                  position="top-start"
                  withArrow>
                  <ActionIcon variant="filled" size="sm" bg="plum.3" c="plum">
                    <IconCurrencyDollar size={16} />
                  </ActionIcon>
                </Tooltip>
              )}
          </div>
          <div className={`${styles.fieldRow} ${styles.rightFields}`}>
            <div className={styles.field}>
            <LabelText label="Report Type" variant="sentence" boxProps={{ mb: 2 }} />
            <SwitchTabs
              options={[
                { label: 'Remote', id: 'remote' },
                { label: 'In Clinic', id: 'in-clinic' }
              ]}
              value={memoizedData.isInClinic ? 'in-clinic' : 'remote'}
              onChange={handleAppointmentTypeTabClick}
            />
            </div>
            <div className={styles.field}>
            <LabelText label="Appointment Type" variant="sentence" boxProps={{ mb: 2 }} />
            <Dropdown
              isMulti={false}
              options={appointmentTypeOptions}
              value={reportTypeOptions.find((option) => option.value === reportType) || undefined}
              onChange={handleReportTypeChange}
              placeholder="Select Appointment Type"
            />
            </div>
            <div className={styles.field}>
            <LabelText label="Alert Type" variant="sentence" boxProps={{ mb: 2 }} />
            <Dropdown
              isMulti={false}
              options={alertColorTypeOptions}
              value={
                alertColorTypeOptions.find((option) => option.value === alertColorType) || undefined
              }
              onChange={handleAlertTypeChange}
              placeholder="Select Alert Type"
            />
            </div>
          </div>

          <div className={`${styles.fieldRow} ${styles.leftFields}`}>
            <div className={styles.field}>
            <LabelText label="Date of Service" variant="sentence" boxProps={{ mb: 4 }} />
            <DatePickerInput
              placeholder="MM/DD/YYYY"
              required
              value={
                report.billing?.billingDate ? dayjs(report.billing.billingDate).toDate() : null
              }
              maxDate={new Date()}
              onChange={handleDateChange}
              rightSection={<IconCalendar size={18} />}
              disabled={isUpdatingBillingDate}
              clearable={false}
              valueFormat="MM/DD/YYYY"
            />
            </div>
            <div className={styles.field}>
            <LabelText label="Transmission Selected" variant="sentence" boxProps={{ mb: 2 }} />
            <TransmissionSelection reportId={reportId} patientId={report.patient.id} />
            </div>
          {showAppointmentField && report?.matchDevice?.id && (
            <div className={styles.field}>
              <LabelText label="Appointment Selected" variant="sentence" boxProps={{ mb: 2 }} />
              <AppointmentSelection
                reportId={reportId}
                patientId={report.patient.id}
                deviceId={report.matchDevice.id}
                appointment={appointment}
                savedAppointmentId={report.appointment?.id}
                handleChange={handleAppointmentChange}
                onEligibleAppointments={setEligibleAppointments}
                onAppointmentLocked={setAppointmentLocked}
                onCurrentAppointmentEncounter={fillEncounterFromAppointment}
                inClinicOnly={isReportInClinic(report.transmissionType)}
                requireSelection={requireVisitToSign}
                isDisabled={
                  report.signatureStatus === SignatureStatus.Signed ||
                  report.signatureStatus === SignatureStatus.NonPractitionerSigned
                }
              />
            </div>
          )}
          {shouldShowEncounterId && (
            <div className={`${styles.field} ${styles.encounterField}`}>
              <LabelText label="Encounter ID" variant="sentence" boxProps={{ mb: 2 }} />
              <EncounterIdField
                key={reportId}
                value={encounterId}
                options={encounterOptions}
                disabled={
                  report.signatureStatus === SignatureStatus.Signed ||
                  report.signatureStatus === SignatureStatus.NonPractitionerSigned
                }
                requireSelection={requireVisitToSign}
                onSelect={selectEncounter}
                onSave={saveEnteredEncounter}
              />
            </div>
          )}
          </div>
          <div className={`${styles.fieldRow} ${styles.rightFields}`}>
          <div className={styles.field}>{billableControl}</div>

          <div className={`${styles.field} ${showBillingCodes ? '' : styles.fieldReserved}`}>
                <LabelText label="Professional Codes" variant="sentence" boxProps={{ mb: 2 }} />
                <Dropdown
                  isMulti={true}
                  options={memoizedData.professionalOptions || []}
                  value={
                    professionalCodes
                      .map((code) =>
                        (memoizedData.professionalOptions || []).find((opt) => opt.value === code)
                      )
                      .filter(Boolean) as LabelAndValue[]
                  }
                  onChange={async (selected) => {
                    const selectedCodes = selected?.map((item) => item.value) ?? [];
                    setProfessionalCodes(selectedCodes);
                    await handleBillingCodesChange('professionalCodes', selected);
                  }}
                  placeholder={professionalCodes.length === 0 ? 'Select Codes' : ''}
                  isDisabled={
                    areFieldsEditable ? isBillableUpdating || disableCptCodesDropdown : false
                  }
                  customComponents={{ Option: BillingCodeOption }}
                />
              </div>
              <div className={`${styles.field} ${showBillingCodes ? '' : styles.fieldReserved}`}>
                <LabelText label="Technical Code" variant="sentence" boxProps={{ mb: 2 }} />
                {reportType === 'COMBO' ? (
                  <Dropdown
                    isMulti={true}
                    options={memoizedData.technicalOptions || []}
                    value={
                      technicalCodes
                        .map((code) =>
                          (memoizedData.technicalOptions || []).find((opt) => opt.value === code)
                        )
                        .filter(Boolean) as LabelAndValue[]
                    }
                    onChange={async (selected: any) => {
                      const selectedValues = Array.isArray(selected)
                        ? selected.map((item) => item.value)
                        : selected
                          ? [selected.value]
                          : [];
                      setTechnicalCodes(selectedValues);
                      await handleBillingCodesChange('technicalCodes', selectedValues);
                    }}
                    placeholder={'Select Codes'}
                    isDisabled={
                      areFieldsEditable ? isBillableUpdating || disableCptCodesDropdown : false
                    }
                  />
                ) : (
                  <Dropdown
                    isMulti={false}
                    options={memoizedData.technicalOptions || []}
                    value={
                      technicalCodes.length > 0
                        ? (memoizedData.technicalOptions || []).find(
                            (opt) => opt.value === technicalCodes[0]
                          ) || { label: '-', value: '-' }
                        : { label: '-', value: '-' }
                    }
                    onChange={async (selected) => {
                      const selectedValues = Array.isArray(selected)
                        ? selected.map((item) => item.value)
                        : selected
                          ? [selected.value]
                          : [];
                      setTechnicalCodes(selectedValues);
                      await handleBillingCodesChange('technicalCodes', selectedValues);
                    }}
                    placeholder="Select Code"
                    isDisabled={
                      areFieldsEditable ? isBillableUpdating || disableCptCodesDropdown : false
                    }
                  />
                )}
              </div>
          </div>
        </div>
      </div>

      {report?.transmission?.autoTriageEvaluationId && (
        <TriageAuditLogModal
          opened={isTriageModalOpen}
          onClose={() => setIsTriageModalOpen(false)}
          triageAuditLogId={report.transmission.autoTriageEvaluationId}
        />
      )}

      <ConfirmationModal
        isOpen={isModalOpen}
        onClose={handleCancelAppointmentTypeChange}
        title="Warning Modal"
        description={`Are you sure you want switch the report type from ${memoizedData.isInClinic ? 'In-Clinic' : 'Remote'} to ${pendingAppointmentType === 'in-clinic' ? 'In-Clinic' : 'Remote'}?`}
        onConfirm={() => {
          if (pendingAppointmentType) {
            handleAppointmentTypeChange(pendingAppointmentType);
          }
        }}
        isLoading={isAppointmentTypeLoading}
      />
    </div>
  );
};

export default ReportDetails;
